import { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import api, { formatCurrency, downloadAuthedFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import {
  ScanLine,
  Search,
  Trash2,
  Plus,
  Minus,
  ShoppingCart,
  X,
  Camera,
  Keyboard,
  Receipt,
  Download,
  Send,
  Mail,
  CheckCircle2,
} from "lucide-react";

/**
 * POS (Point of Sale) — full-screen cashier terminal.
 *
 * Layout (desktop ≥1024px):
 *   ┌──────────────────────────────────┬────────────────────────┐
 *   │ Live camera scanner (or manual)  │  Cart (scanned items)  │
 *   │                                  │  - line items          │
 *   │ Recently scanned chip            │  - qty edit            │
 *   │                                  │  - subtotal/discount   │
 *   │                                  │  - customer picker     │
 *   │                                  │  - Checkout button     │
 *   └──────────────────────────────────┴────────────────────────┘
 *
 * Mobile (<1024px): scanner stacks above cart.
 *
 * Scanning behaviour:
 *   - Same SKU scanned twice → bump qty (no duplicate lines)
 *   - 800 ms debounce so a single QR scan doesn't add 3 lines
 *   - Audible beep on successful add (browser AudioContext beep)
 *
 * Backend reuse: cart submits via POST /api/accounting/sales/multi — same
 * endpoint as Record Sale, so commissions, audit log, stock decrement and
 * invoice PDF all "just work".
 */
export default function POS() {
  const { user } = useAuth();
  const [mode, setMode] = useState("camera");  // 'camera' | 'keyboard'
  const [cart, setCart] = useState([]);  // [{product, qty, unit_price, ...}]
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [walkIn, setWalkIn] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [invoiceDiscount, setInvoiceDiscount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [recentChips, setRecentChips] = useState([]);  // last 3 scans for visual feedback
  const [shareSale, setShareSale] = useState(null);    // {sale_id, invoice_number, total, customer} after checkout
  const [variantPickerProd, setVariantPickerProd] = useState(null);  // product currently in variant-picker
  const lastScanAtRef = useRef(0);
  const beepRef = useRef(null);

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data || []))
      .catch(() => setCustomers([]));
  }, []);

  // ---- Scan handler (debounced + de-duplicating) -----------------------
  const handleScan = async (raw) => {
    // Debounce: ignore scans that arrive within 800ms of the last one.
    const now = Date.now();
    if (now - lastScanAtRef.current < 800) return;
    lastScanAtRef.current = now;

    // Accept either a full URL (preferred QR payload) or a bare short_id.
    let shortId = (raw || "").trim();
    try {
      if (shortId.includes("/product/")) {
        shortId = shortId.split("/product/").pop().split(/[?#]/)[0];
      } else if (shortId.includes("/")) {
        shortId = shortId.split("/").pop().split(/[?#]/)[0];
      }
    } catch { /* fall through */ }
    shortId = shortId.toUpperCase();
    if (!shortId) return;

    try {
      const { data: prod } = await api.get(`/lookup/by-qr/${shortId}`);
      addToCart(prod);
      _beep();
      setLastScan({ sku: prod.sku, ts: now });
      setRecentChips((prev) => [
        { sku: prod.sku, name: prod.name, ts: now },
        ...prev.slice(0, 2),
      ]);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Unknown QR: ${shortId}`);
    }
  };

  const addToCart = (prod) => {
    // Variant logic — three cases:
    //   1. 0 or 1 variant defined → straight to cart with that variant (or null).
    //   2. 2+ variants but only ONE is in stock → auto-pick the in-stock one.
    //      The picker would be a forced "tap the only non-greyed-out button"
    //      moment. Skip it. This is the common case once a SKU sells down.
    //   3. 2+ variants, 2+ in stock → open the picker so the cashier chooses.
    const variants = Array.isArray(prod.variants) ? prod.variants : [];
    const inStock = variants.filter((v) => {
      const s = v.stock != null ? v.stock : (v.quantity || 0);
      return Number(s) > 0;
    });
    if (variants.length > 1 && inStock.length > 1) {
      setVariantPickerProd(prod);
      return;
    }
    // Prefer the only in-stock variant; if none are in stock, fall through to
    // variants[0] so the cashier can still see the OOS warning in the cart.
    const v = inStock[0] || variants[0] || null;
    addToCartWithVariant(prod, v);
  };

  const addToCartWithVariant = (prod, variant) => {
    setCart((prev) => {
      // Same SKU + same variant → bump qty. Different variants are kept as
      // separate cart lines so partial-fill orders stay clean.
      const variantId = variant?.id || "";
      const idx = prev.findIndex(
        (l) => l.product_id === prod.id && l.variant_id === variantId,
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        toast.success(`+ ${prod.sku} (${next[idx].qty}×)`);
        return next;
      }
      const unitPrice = Number(variant?.selling_price ?? prod.selling_price ?? 0);
      const variantLabel = variant
        ? [variant.color, variant.size_mm ? `${variant.size_mm}mm` : variant.size_label].filter(Boolean).join(" · ")
        : "";
      // Variant stock falls back to legacy `quantity` field for un-migrated rows.
      const variantStock = variant
        ? (variant.stock != null ? variant.stock : variant.quantity)
        : prod.stock;
      toast.success(`+ ${prod.sku}${variantLabel ? ` · ${variantLabel}` : ""}`);
      return [
        ...prev,
        {
          product_id: prod.id,
          variant_id: variantId,
          variant_label: variantLabel,
          sku: prod.sku,
          name: prod.name,
          stock: variantStock,
          unit: prod.unit || "pcs",  // ct / g / kg / pcs — shown next to qty
          qty: 1,
          unit_price: unitPrice,
        },
      ];
    });
  };

  const _beep = () => {
    try {
      if (!beepRef.current) beepRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = beepRef.current;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 1100; o.type = "sine";
      g.gain.setValueAtTime(0.18, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      o.start(); o.stop(ctx.currentTime + 0.12);
    } catch { /* ignore on browsers w/o WebAudio */ }
  };

  const updateQty = (idx, qty) => {
    setCart((prev) => {
      const next = [...prev];
      // Allow decimals (e.g. 120.56 ct out of 1000.20 ct stock) and any value
      // ≥ 0 (lets the cashier blank the field while typing). The empty-string
      // case is handled by the input's onChange which passes 0 — that's fine,
      // grand total simply shows 0 until they finish typing.
      const v = typeof qty === "number" ? qty : parseFloat(qty);
      next[idx] = { ...next[idx], qty: isNaN(v) || v < 0 ? 0 : v };
      return next;
    });
  };

  const updatePrice = (idx, unit_price) => {
    setCart((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], unit_price: Math.max(0, Number(unit_price) || 0) };
      return next;
    });
  };

  const removeLine = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));
  const clearCart = () => { setCart([]); setInvoiceDiscount(0); setLastScan(null); };

  const subtotal = useMemo(
    () => cart.reduce((sum, l) => sum + l.qty * l.unit_price, 0),
    [cart],
  );
  const discount = Math.min(Number(invoiceDiscount) || 0, subtotal);
  const grandTotal = subtotal - discount;
  const itemCount = useMemo(() => cart.reduce((n, l) => n + l.qty, 0), [cart]);

  const submit = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (!walkIn && !customerId) return toast.error("Select a customer or use Walk-in");
    setSubmitting(true);
    try {
      const payload = {
        items: cart.map((l) => ({
          product_id: l.product_id,
          variant_id: l.variant_id || "",
          quantity: l.qty,
          unit_price: l.unit_price,
        })),
        customer_id: walkIn ? "" : customerId,
        custom_customer_name: walkIn ? "Walk-in Customer" : "",
        custom_customer_phone: walkIn ? "0000000000" : "",
        custom_customer_address: "",
        invoice_discount: discount,
        tax_pct: 0,
        payment_method: paymentMethod,
        notes: "POS terminal sale",
        initial_payment_amount: grandTotal,  // POS = paid in full at checkout
        initial_payment_type: "Balance",
      };
      const { data } = await api.post("/accounting/sales/multi", payload);
      toast.success(`Sale ${data.invoice_number || data.id} created`);
      // Show post-checkout share modal so cashier can WA / Email the invoice
      // PDF to the customer right away (the most common ask after ringing up
      // a sale). Cart is cleared so the next customer can start scanning.
      const selectedCustomer = walkIn ? null : customers.find((c) => c.id === customerId);
      setShareSale({
        id: data.id,
        invoice_number: data.invoice_number,
        total: grandTotal,
        customer_name: walkIn ? "Walk-in Customer" : (selectedCustomer?.name || ""),
        customer_phone: walkIn ? "" : (selectedCustomer?.phone || ""),
        // Pre-fill email from CRM record so saved customers never need re-typing.
        customer_email: walkIn ? "" : (selectedCustomer?.email || ""),
      });
      clearCart();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Layout title="POS Terminal" subtitle="Scan, ring up, checkout">
      <Toaster theme="dark" position="top-right" />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,420px] gap-6">
        {/* LEFT — Scanner */}
      <ScannerPanel
        mode={mode}
        setMode={setMode}
        onScan={handleScan}
        onPickProduct={addToCart}
        lastScan={lastScan}
        recentChips={recentChips}
      />
        {/* RIGHT — Cart + Checkout */}
        <CartPanel
          cart={cart}
          itemCount={itemCount}
          subtotal={subtotal}
          discount={discount}
          invoiceDiscount={invoiceDiscount}
          setInvoiceDiscount={setInvoiceDiscount}
          grandTotal={grandTotal}
          updateQty={updateQty}
          updatePrice={updatePrice}
          removeLine={removeLine}
          clearCart={clearCart}
          customers={customers}
          customerId={customerId}
          setCustomerId={setCustomerId}
          walkIn={walkIn}
          setWalkIn={setWalkIn}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          submit={submit}
          submitting={submitting}
        />
      </div>
      {shareSale && (
        <PostCheckoutShareModal
          sale={shareSale}
          onClose={() => setShareSale(null)}
        />
      )}
      {variantPickerProd && (
        <VariantPickerModal
          product={variantPickerProd}
          onPick={(variant) => {
            addToCartWithVariant(variantPickerProd, variant);
            setVariantPickerProd(null);
          }}
          onClose={() => setVariantPickerProd(null)}
        />
      )}
    </Layout>
  );
}

/* ───────────────────────── Scanner Panel ───────────────────────── */
function ScannerPanel({ mode, setMode, onScan, onPickProduct, lastScan, recentChips }) {
  const containerId = "pos-scanner-region";
  const scannerRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [manual, setManual] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const inputRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (mode === "keyboard") inputRef.current?.focus();
    if (mode === "search") searchInputRef.current?.focus();
  }, [mode]);

  // Debounced product search — fires 300 ms after the user stops typing
  // so we don't pound the API on every keystroke.
  useEffect(() => {
    if (mode !== "search") return;
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products?search=${encodeURIComponent(q)}&limit=12`);
        // The endpoint sometimes returns an array, sometimes {items: []}
        const items = Array.isArray(data) ? data : (data.items || []);
        setSearchResults(items);
      } catch (e) {
        console.warn("Product search failed:", e);
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, mode]);

  useEffect(() => {
    if (mode !== "camera") return;
    let cancelled = false;
    let scanner;
    setCameraError(null);
    setCameraReady(false);

    (async () => {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");
        if (cancelled) return;
        scanner = new Html5Qrcode(containerId, {
          verbose: false,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
          ],
        });
        scannerRef.current = scanner;
        // Generous qrbox so cashiers don't need to align the QR perfectly.
        const qrboxFn = (w, h) => {
          const min = Math.min(w, h);
          const size = Math.max(220, Math.floor(min * 0.72));
          return { width: size, height: size };
        };
        await scanner.start(
          { facingMode: "environment" },
          {
            fps: 15,
            qrbox: qrboxFn,
            aspectRatio: 1.333,
            // Native BarcodeDetector when available (Chrome Android / iOS 17+)
            // is dramatically faster + more reliable than the JS fallback.
            experimentalFeatures: { useBarCodeDetectorIfSupported: true },
            disableFlip: false,
          },
          (decoded) => { onScan(decoded); },
          () => { /* ignore per-frame failures */ },
        );
        if (!cancelled) setCameraReady(true);
      } catch (e) {
        console.error("POS scanner init failed:", e);
        if (!cancelled) {
          setCameraError(e?.message || "Could not start camera");
          setMode("keyboard");
        }
      }
    })();

    return () => {
      cancelled = true;
      // Wrap stop in a guard — Html5Qrcode.stop() throws synchronously when
      // the scanner never actually started (e.g. denied camera permission).
      try {
        const s = scannerRef.current;
        if (s && s.getState && s.getState() >= 2) {
          s.stop().catch(() => {});
        }
      } catch { /* swallow */ }
    };
  }, [mode, onScan, setMode]);

  const submitManual = (e) => {
    e?.preventDefault?.();
    if (!manual.trim()) return;
    onScan(manual.trim());
    setManual("");
    inputRef.current?.focus();
  };

  return (
    <div className="space-y-4">
      {/* Mode tabs */}
      <div className="flex border border-white/10">
        <button
          onClick={() => setMode("camera")}
          data-testid="pos-mode-camera"
          className={`flex-1 py-3 px-3 inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider ${
            mode === "camera" ? "bg-[#D4AF37]/10 text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-[#8A8A8A]"
          }`}
        >
          <Camera className="w-3.5 h-3.5" strokeWidth={1.5} /> Camera
        </button>
        <button
          onClick={() => setMode("keyboard")}
          data-testid="pos-mode-keyboard"
          className={`flex-1 py-3 px-3 inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider ${
            mode === "keyboard" ? "bg-[#D4AF37]/10 text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-[#8A8A8A]"
          }`}
        >
          <Keyboard className="w-3.5 h-3.5" strokeWidth={1.5} /> Manual
        </button>
        <button
          onClick={() => setMode("search")}
          data-testid="pos-mode-search"
          className={`flex-1 py-3 px-3 inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider ${
            mode === "search" ? "bg-[#D4AF37]/10 text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-[#8A8A8A]"
          }`}
        >
          <Search className="w-3.5 h-3.5" strokeWidth={1.5} /> Search
        </button>
      </div>

      {mode === "camera" ? (
        <div className="border border-white/10 bg-black p-2" data-testid="pos-camera-panel">
          <div id={containerId} className="w-full" style={{ minHeight: "320px" }} />
          <div className="text-[0.7rem] text-[#8A8A8A] mt-3 text-center leading-relaxed">
            {cameraReady ? (
              <>Hold the hangtag QR ~15 cm from the camera. Scanning is continuous — keeps adding while open.</>
            ) : cameraError ? (
              <span className="text-red-400">Camera unavailable: {cameraError}</span>
            ) : (
              <>Waiting for camera permission…</>
            )}
          </div>
        </div>
      ) : mode === "keyboard" ? (
        <div className="border border-white/10 p-5" data-testid="pos-manual-panel">
          <form onSubmit={submitManual} className="space-y-3">
            <label className="block">
              <div className="overline mb-2">Scan with hardware scanner OR type SKU short ID</div>
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  className="luxe-input flex-1 px-4 py-3 text-lg mono uppercase"
                  placeholder="e.g. EUTVUHSQ"
                  data-testid="pos-manual-input"
                  autoFocus
                />
                <button
                  type="submit"
                  className="btn-gold px-5 py-3"
                  data-testid="pos-manual-submit"
                >
                  <ScanLine className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </label>
            <div className="text-[0.7rem] text-[#8A8A8A]">
              A USB / Bluetooth barcode scanner acts as a keyboard — it'll type into the box above and press Enter automatically.
            </div>
          </form>
        </div>
      ) : (
        <div className="border border-white/10 p-5" data-testid="pos-search-panel">
          <label className="block mb-3">
            <div className="overline mb-2">Search product by name or SKU</div>
            <div className="relative">
              <Search className="w-4 h-4 text-[#666] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ruby cutting, sapphire, OJ-00123…"
                className="luxe-input w-full pl-10 pr-4 py-3 text-base"
                data-testid="pos-search-input"
                autoFocus
              />
            </div>
          </label>
          <div className="text-[0.7rem] text-[#8A8A8A] mb-3">
            Tap any result to add it to the cart — useful when a hangtag QR is damaged or unreadable.
          </div>
          <div className="space-y-1.5 max-h-[360px] overflow-y-auto" data-testid="pos-search-results">
            {searchQuery.trim().length < 2 ? (
              <div className="text-center text-[#666] text-xs py-8">Type at least 2 characters to search…</div>
            ) : searchResults.length === 0 ? (
              <div className="text-center text-[#666] text-xs py-8">No products match.</div>
            ) : (
              searchResults.map((p) => {
                const stock = Number(p.stock || 0);
                const oos = stock <= 0;
                const thumb = Array.isArray(p.photos) && p.photos.length > 0 ? p.photos[0] : null;
                return (
                  <button
                    key={p.id}
                    onClick={() => {
                      onPickProduct(p);
                      setSearchQuery("");
                      setSearchResults([]);
                      searchInputRef.current?.focus();
                    }}
                    data-testid={`pos-search-result-${p.sku}`}
                    className={`w-full text-left border px-3 py-2.5 transition-colors ${
                      oos
                        ? "border-red-400/20 bg-red-400/[0.03] hover:bg-red-400/[0.08]"
                        : "border-white/5 bg-white/[0.02] hover:bg-[#D4AF37]/[0.05] hover:border-[#D4AF37]/30"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Thumbnail — small square preview. Lazy-loaded so a
                          50-result search doesn't pull 50 images at once. */}
                      <div className="w-12 h-12 shrink-0 border border-white/10 bg-white/[0.03] flex items-center justify-center overflow-hidden">
                        {thumb ? (
                          <img
                            src={thumb}
                            alt={p.name}
                            loading="lazy"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // If the image 404s, swap to the gem icon
                              // fallback so the row still looks clean.
                              e.currentTarget.style.display = "none";
                              e.currentTarget.parentElement.querySelector(".thumb-fallback").style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          className="thumb-fallback w-full h-full items-center justify-center text-[#444]"
                          style={{ display: thumb ? "none" : "flex" }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M6 3 2 9l10 12L22 9l-4-6Z" />
                            <path d="M11 3 8 9l4 12 4-12-3-6" />
                            <path d="M2 9h20" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex justify-between items-start gap-2 flex-1 min-w-0">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-[#EAEAEA] truncate">{p.name}</div>
                          <div className="text-[0.65rem] mono text-[#666] mt-0.5">
                            {p.sku} · {p.category || "—"}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="mono text-xs text-[#D4AF37]">
                            {formatCurrency(p.selling_price || 0)}
                          </div>
                          <div className={`text-[0.6rem] mono mt-0.5 ${oos ? "text-red-400" : "text-[#8A8A8A]"}`}>
                            {oos ? "OUT OF STOCK" : `${stock} ${p.unit || "pcs"} avail`}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Recently scanned chips */}
      {recentChips.length > 0 && (
        <div className="space-y-1.5" data-testid="pos-recent">
          <div className="overline text-[0.55rem]">Recent scans</div>
          {recentChips.map((c) => (
            <div
              key={c.ts}
              className={`flex items-center justify-between border border-[#7BA585]/30 bg-[#7BA585]/[0.05] px-3 py-2 text-xs ${
                lastScan?.sku === c.sku ? "fade-up" : ""
              }`}
            >
              <span className="mono text-[#7BA585]">+ {c.sku}</span>
              <span className="text-[#8A8A8A] truncate ml-3">{c.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── Cart Panel ───────────────────────── */
function CartPanel({
  cart, itemCount, subtotal, discount, invoiceDiscount, setInvoiceDiscount,
  grandTotal, updateQty, updatePrice, removeLine, clearCart,
  customers, customerId, setCustomerId, walkIn, setWalkIn,
  paymentMethod, setPaymentMethod, submit, submitting,
}) {
  return (
    <aside className="border border-white/10 bg-[#0A0A0A] flex flex-col lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)]"
           data-testid="pos-cart-panel">
      {/* Header */}
      <header className="border-b border-white/10 px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
          <span className="overline text-[#D4AF37]">Cart</span>
          <span className="text-xs text-[#8A8A8A] mono" data-testid="cart-count">
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </span>
        </div>
        {cart.length > 0 && (
          <button
            onClick={clearCart}
            data-testid="cart-clear"
            className="text-[0.65rem] text-[#666] hover:text-red-400 uppercase tracking-wider"
          >
            Clear
          </button>
        )}
      </header>

      {/* Items */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 min-h-[200px]">
        {cart.length === 0 ? (
          <div className="text-center py-16 text-[#666] text-sm">
            <ShoppingCart className="w-8 h-8 mx-auto mb-3 text-[#333]" strokeWidth={1} />
            Cart is empty — scan a hangtag to start
          </div>
        ) : (
          cart.map((l, idx) => (
            <div key={`${l.product_id}-${l.variant_id}-${idx}`} className="border border-white/5 bg-white/[0.02] px-3 py-2.5"
                 data-testid={`cart-line-${idx}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#EAEAEA] truncate">{l.name}</div>
                  <div className="text-[0.65rem] mono text-[#666] mt-0.5">
                    {l.sku}
                    {l.variant_label ? (
                      <span className="text-[#D4AF37] ml-1.5" data-testid={`cart-variant-${idx}`}>
                        · {l.variant_label}
                      </span>
                    ) : null}
                  </div>
                </div>
                <button
                  onClick={() => removeLine(idx)}
                  className="inline-flex items-center justify-center w-8 h-8 border border-red-400/30 bg-red-400/5 text-red-400 hover:bg-red-400/20 hover:border-red-400/60 transition-colors shrink-0"
                  title="Remove this item from cart"
                  data-testid={`cart-remove-${idx}`}
                  aria-label={`Remove ${l.name}`}
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex items-center justify-between gap-2 mt-2">
                <div className="flex items-center border border-white/10">
                  <button
                    onClick={() => updateQty(idx, Math.max(0, l.qty - 1))}
                    className="px-2 py-1 text-[#8A8A8A] hover:text-[#D4AF37]"
                    data-testid={`cart-qty-minus-${idx}`}
                  >
                    <Minus className="w-3 h-3" strokeWidth={2} />
                  </button>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={l.qty}
                    onChange={(e) => {
                      // Allow free-form typing including decimals. Empty
                      // string passes 0 (so the input doesn't lock at 1).
                      const raw = e.target.value;
                      if (raw === "") return updateQty(idx, 0);
                      updateQty(idx, parseFloat(raw));
                    }}
                    className="w-16 bg-transparent text-center text-sm mono py-1"
                    data-testid={`cart-qty-${idx}`}
                  />
                  <button
                    onClick={() => updateQty(idx, l.qty + 1)}
                    className="px-2 py-1 text-[#8A8A8A] hover:text-[#D4AF37]"
                    data-testid={`cart-qty-plus-${idx}`}
                  >
                    <Plus className="w-3 h-3" strokeWidth={2} />
                  </button>
                </div>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={l.unit_price}
                  onChange={(e) => updatePrice(idx, e.target.value)}
                  className="luxe-input w-32 px-2 py-1 text-xs mono text-right"
                  data-testid={`cart-price-${idx}`}
                />
              </div>
              <div className="text-right mt-1 text-xs mono text-[#D4AF37]">
                {l.qty > 0 && (
                  <span className="text-[#666] mr-2">
                    {l.qty} {l.unit || "pcs"} × {formatCurrency(l.unit_price)}
                  </span>
                )}
                = {formatCurrency(l.qty * l.unit_price)}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Customer + Payment */}
      <div className="border-t border-white/5 px-4 py-3 space-y-3 text-xs">
        <div>
          <div className="overline mb-1.5">Customer</div>
          <div className="flex items-center gap-2 mb-2">
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={walkIn}
                onChange={(e) => setWalkIn(e.target.checked)}
                className="accent-[#D4AF37]"
                data-testid="pos-walkin"
              />
              <span className="text-[#8A8A8A]">Walk-in</span>
            </label>
          </div>
          {!walkIn && (
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="luxe-input w-full px-3 py-2 text-xs"
              data-testid="pos-customer"
            >
              <option value="">— Select customer —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name} {c.phone ? `· ${c.phone}` : ""}</option>
              ))}
            </select>
          )}
        </div>
        <div>
          <div className="overline mb-1.5">Payment Method</div>
          <div className="grid grid-cols-4 gap-1">
            {["Cash", "Wire", "Card", "Crypto"].map((m) => (
              <button
                key={m}
                onClick={() => setPaymentMethod(m)}
                data-testid={`pos-pay-${m}`}
                className={`py-1.5 text-[0.6rem] uppercase tracking-wider border ${
                  paymentMethod === m
                    ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/[0.08]"
                    : "border-white/10 text-[#8A8A8A]"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Totals + Checkout */}
      <div className="border-t border-[#D4AF37]/20 bg-[#0D0D0D] px-5 py-4 space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-[#8A8A8A]">Subtotal</span>
          <span className="mono">{formatCurrency(subtotal)}</span>
        </div>
        <div className="flex justify-between items-center text-xs">
          <span className="text-[#8A8A8A]">Discount</span>
          <input
            type="number"
            min="0"
            step="1000"
            value={invoiceDiscount}
            onChange={(e) => setInvoiceDiscount(e.target.value)}
            className="luxe-input w-32 px-2 py-1 text-xs mono text-right"
            data-testid="pos-discount"
          />
        </div>
        <div className="border-t border-[#D4AF37]/30 pt-2 flex justify-between items-baseline">
          <span className="overline text-[#D4AF37]">Total</span>
          <span className="serif text-2xl text-[#D4AF37]" data-testid="pos-total">
            {formatCurrency(grandTotal)}
          </span>
        </div>
        <button
          onClick={submit}
          disabled={submitting || cart.length === 0}
          className="w-full btn-gold py-3 mt-3 inline-flex items-center justify-center gap-2"
          data-testid="pos-checkout"
        >
          <Receipt className="w-4 h-4" strokeWidth={1.5} />
          {submitting ? "Processing…" : "Checkout"}
        </button>
      </div>
    </aside>
  );
}

/* ─────────────────── Post-Checkout Share Modal ─────────────────── */
/**
 * Opens immediately after a successful POS sale. Cashier can:
 *   - Download invoice PDF
 *   - Share via WhatsApp (uses the Web Share API with the PDF when
 *     supported on the device — iOS Safari 16.4+ / Chrome Android. Falls
 *     back to wa.me text on desktop / older browsers, where the cashier
 *     manually attaches the downloaded PDF).
 *   - Email (Web Share with file → falls back to mailto with summary)
 *
 * Walk-in customers don't have a phone on file, so the WhatsApp button
 * shows a small "phone?" input above it. Saved customers prefill.
 */
function PostCheckoutShareModal({ sale, onClose }) {
  const [phone, setPhone] = useState(sale.customer_phone || "");
  const [email, setEmail] = useState(sale.customer_email || "");
  const [pdfBlob, setPdfBlob] = useState(null);

  // Pre-fetch the PDF as a Blob so share + download are instant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `${api.defaults.baseURL}/accounting/invoice/${sale.id}`;
        const token = localStorage.getItem("gv_token") || localStorage.getItem("token");
        const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) throw new Error("PDF fetch failed");
        const blob = await res.blob();
        if (!cancelled) setPdfBlob(blob);
      } catch (e) {
        console.warn("Could not preload invoice PDF:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [sale.id]);

  const filename = `invoice_${sale.invoice_number || sale.id}.pdf`;
  const summary = `Hi ${sale.customer_name || "there"}, here is your invoice ${sale.invoice_number || ""} from Opik Jewels — Total ${formatCurrency(sale.total)}. Terima kasih!`;

  const handleDownload = async () => {
    try {
      await downloadAuthedFile(
        `/accounting/invoice/${sale.id}`,
        filename,
        { errorMessage: "Download failed" },
      );
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  // ----- WhatsApp -----
  const handleWhatsApp = async () => {
    // Prefer Web Share API with the PDF attached — that gives the cashier
    // a single "share to WhatsApp / Telegram / anywhere" sheet on iOS/Android.
    if (pdfBlob && navigator.canShare) {
      try {
        const file = new File([pdfBlob], filename, { type: "application/pdf" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: filename,
            text: summary,
          });
          return;
        }
      } catch (e) {
        // user cancelled or share unsupported — fall through
        if (e.name !== "AbortError") console.warn("share fallback:", e);
      }
    }
    // Fallback: open WhatsApp with the message; cashier manually attaches PDF
    // after pressing the download button.
    const cleanPhone = (phone || "").replace(/[^\d+]/g, "");
    const waUrl = cleanPhone
      ? `https://wa.me/${cleanPhone}?text=${encodeURIComponent(summary)}`
      : `https://wa.me/?text=${encodeURIComponent(summary)}`;
    // Trigger a download alongside so the cashier has the file ready.
    handleDownload();
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  // ----- Email -----
  const handleEmail = async () => {
    if (pdfBlob && navigator.canShare) {
      try {
        const file = new File([pdfBlob], filename, { type: "application/pdf" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: filename,
            text: summary,
          });
          return;
        }
      } catch (e) {
        if (e.name !== "AbortError") console.warn("share fallback:", e);
      }
    }
    // Fallback: open mailto with the summary; cashier attaches the PDF
    // (which we trigger as a download in parallel).
    const mailto = `mailto:${encodeURIComponent(email || "")}?subject=${encodeURIComponent(`Invoice ${sale.invoice_number || ""}`)}&body=${encodeURIComponent(summary)}`;
    handleDownload();
    window.location.href = mailto;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
         data-testid="post-checkout-share-modal">
      <div className="bg-[#0F0F0F] border border-[#D4AF37]/30 w-full max-w-md fade-up">
        {/* Header */}
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-start">
          <div>
            <div className="inline-flex items-center gap-2 overline text-[#7BA585]">
              <CheckCircle2 className="w-3.5 h-3.5" /> Sale Confirmed
            </div>
            <h3 className="serif text-xl mt-1" data-testid="share-invoice-number">
              {sale.invoice_number || "Invoice"}
            </h3>
            <div className="text-xs text-[#8A8A8A] mt-1 mono">
              {sale.customer_name || "Walk-in"} · {formatCurrency(sale.total)}
            </div>
          </div>
          <button onClick={onClose} className="text-[#8A8A8A] hover:text-[#EAEAEA]" data-testid="share-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-7 py-6 space-y-5">
          {/* Download */}
          <button
            onClick={handleDownload}
            data-testid="share-download"
            className="w-full btn-outline-gold py-3 inline-flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" strokeWidth={1.5} />
            Download Invoice PDF
          </button>

          {/* WhatsApp */}
          <div>
            <div className="overline mb-2">Customer phone (optional)</div>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+62 812 3456 7890"
              className="luxe-input w-full px-4 py-2.5 text-sm mono mb-2"
              data-testid="share-phone"
            />
            <button
              onClick={handleWhatsApp}
              data-testid="share-whatsapp"
              className="w-full btn-gold py-3 inline-flex items-center justify-center gap-2 bg-[#25D366] border-[#25D366] hover:bg-[#1da856] hover:border-[#1da856] text-white"
              style={{ background: "#25D366", borderColor: "#25D366", color: "#fff" }}
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
              Share via WhatsApp
            </button>
          </div>

          {/* Email */}
          <div>
            <div className="overline mb-2">Customer email (optional)</div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              className="luxe-input w-full px-4 py-2.5 text-sm mono mb-2"
              data-testid="share-email"
            />
            <button
              onClick={handleEmail}
              data-testid="share-email-btn"
              className="w-full py-3 inline-flex items-center justify-center gap-2 border border-white/15 text-[#EAEAEA] hover:bg-white/[0.05]"
            >
              <Mail className="w-4 h-4" strokeWidth={1.5} />
              Send via Email
            </button>
          </div>

          <div className="border-t border-white/5 pt-3 text-[0.65rem] text-[#666] leading-relaxed">
            On iPhone & Android, "Share via WhatsApp" opens the system share sheet
            with the PDF attached — pick the customer's chat to send. On desktop,
            the PDF auto-downloads and WhatsApp Web opens with a prefilled message.
          </div>
        </div>
      </div>
    </div>
  );
}



/* ───────────────────────── Variant Picker Modal ───────────────────────── */
/** Shown when a scanned/searched product has 2+ variants. Cashier picks the
 *  size/color before the line is created so the right inventory bucket is
 *  decremented at checkout. Out-of-stock variants are disabled. */
function VariantPickerModal({ product, onPick, onClose }) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];

  // Keyboard hotkeys — `1`-`9` picks the Nth in-stock variant, `Esc` closes.
  // Cashiers using a BT keyboard with the iPad can scan → press 1 without
  // lifting their hand. Skip OOS variants so the digit always points at a
  // sellable row.
  useEffect(() => {
    const inStock = variants
      .map((v, i) => ({ v, i, oos: ((v.stock != null ? v.stock : (v.quantity || 0)) <= 0) }))
      .filter((x) => !x.oos);
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      const n = parseInt(e.key, 10);
      if (Number.isFinite(n) && n >= 1 && n <= 9 && inStock[n - 1]) {
        e.preventDefault();
        onPick(inStock[n - 1].v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [variants, onPick, onClose]);
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/85 p-3 sm:p-8 backdrop-blur-sm"
      data-testid="variant-picker-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 w-full max-w-md max-h-[80vh] flex flex-col shadow-2xl">
        <div className="px-4 py-3 border-b border-white/10 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="overline text-[#D4AF37] text-[0.6rem]">Pick Variant</div>
            <div className="text-sm text-[#EAEAEA] truncate mt-0.5" title={product?.name}>
              {product?.name}
            </div>
            <div className="text-[0.65rem] mono text-[#666] mt-0.5">{product?.sku}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#8A8A8A] hover:text-[#D4AF37] p-1 shrink-0"
            data-testid="variant-picker-close"
            aria-label="Close variant picker"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="variant-picker-list">
          {variants.length === 0 ? (
            <div className="text-center text-[#666] text-xs py-8">No variants defined.</div>
          ) : (
            (() => {
              // Pre-compute hotkey digits per row (1..9 for in-stock variants).
              let nextHotkey = 1;
              return variants.map((v) => {
              const label = [v.color, v.size_mm ? `${v.size_mm}mm` : v.size_label].filter(Boolean).join(" · ") || "Default";
              const vStock = v.stock != null ? v.stock : (v.quantity || 0);
              const vUnit = v.stock_unit || product?.unit || "pcs";
              const oos = vStock <= 0;
              const price = Number(v.selling_price ?? product?.selling_price ?? 0);
              const hotkey = oos || nextHotkey > 9 ? null : nextHotkey++;
              return (
                <button
                  type="button"
                  key={v.id}
                  disabled={oos}
                  onClick={() => onPick(v)}
                  data-testid={`variant-pick-${v.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 flex items-center justify-between gap-3 transition-colors ${
                    oos
                      ? "bg-red-400/[0.03] cursor-not-allowed opacity-60"
                      : "hover:bg-[#D4AF37]/[0.06]"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {hotkey && (
                      <span
                        className="shrink-0 w-6 h-6 inline-flex items-center justify-center border border-[#D4AF37]/40 text-[0.65rem] mono text-[#D4AF37]"
                        aria-label={`Press ${hotkey} to pick`}
                      >
                        {hotkey}
                      </span>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm text-[#EAEAEA] truncate">{label}</div>
                      <div className={`text-[0.65rem] mono mt-0.5 ${oos ? "text-red-400" : "text-[#8A8A8A]"}`}>
                        {oos ? "OUT OF STOCK" : `${vStock} ${vUnit} avail`}
                      </div>
                    </div>
                  </div>
                  <div className="mono text-xs text-[#D4AF37] shrink-0">
                    {formatCurrency(price)}
                  </div>
                </button>
              );
              });
            })()
          )}
        </div>
      </div>
    </div>
  );
}
